import React, { useEffect, useMemo, useState } from "react";
import { FiArrowLeft, FiArrowRight, FiCalendar } from "react-icons/fi";
import PageMeta from "../PageMeta";
import { getAllAdImages } from "../../services/advertisingService";
import Newsletter from "../Newsletter";
import { useNavigate } from "react-router-dom";
import Testimonials from "../commonSections/testimonials/Testimonials";
import PopularListings from "../commonSections/PopularListings";
import ActivityCategories from "../commonSections/ActivityCategories";
import { getAllBlogs, getBlogImageUrl } from "../../services/blogService";
import { getBlogs } from "../../services/rssService";
import "./homepage.css";

const ACTIVITIES = [
  { src: "/assets/images/Vector.png",  label: "Art" },
  { src: "/assets/images/Vector1.png", label: "Dance" },
  { src: "/assets/images/Vector2.png", label: "Sports" },
  { src: "/assets/images/Vector3.png", label: "Yoga" },
  { src: "/assets/images/Vector4.png", label: "Summer Camp" },
  { src: "/assets/images/Vector5.png", label: "Academics" },
  { src: "/assets/images/Vector6.png", label: "Tutoring" },
  { src: "/assets/images/Vector7.png", label: "Games / Hobbies" },
  { src: "/assets/images/Vector8.png", label: "Child Care" },
  { src: "/assets/images/Vector9.png", label: "Coding" },
  { src: "/assets/images/Vector10.png", label: "Music" },
  { src: "/assets/images/Vector11.png", label: "STEAM / STEM" },
  { src: "/assets/images/Vector12.png", label: "Birthdays" },
  { src: "/assets/images/Vector13.png", label: "Science" },
  { src: "/assets/images/Vector14.png", label: "Baby / Infants" },
  { src: "/assets/images/Vector15.png", label: "Test Prep" },
  { src: "/assets/images/Vector16.png", label: "Language / Culture" },
  { src: "/assets/images/Vector17.png", label: "Swimming" },
  { src: "/assets/images/Vector18.png", label: "After School" },
  { src: "/assets/images/Vector19.png", label: "Counselling" },
  { src: "/assets/images/Vector20.png", label: "Teen Programs" },
  { src: "/assets/images/Vector21.png", label: "Others" },
];

const chunkArray = (arr, size) => {
  const result = [];
  for (let i = 0; i < arr.length; i += size) result.push(arr.slice(i, i + size));
  return result;
};

function useCarousel(itemsLength, delay) {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (!itemsLength) return;
    const interval = setInterval(() => setIndex((i) => (i + 1) % itemsLength), delay);
    return () => clearInterval(interval);
  }, [itemsLength, delay]);
  const next = () => { if (itemsLength) setIndex((i) => (i + 1) % itemsLength); };
  const prev = () => { if (itemsLength) setIndex((i) => (i - 1 + itemsLength) % itemsLength); };
  return { index, next, prev };
}

function Homepage() {
  const navigate = useNavigate();

  // ads
  const [bannerSubs, setBannerSubs] = useState([]);
  const bannerCarousel = useCarousel(bannerSubs.length, 3000);

  // blogs & news
  const [blogs, setBlogs] = useState([]);
  const [news, setNews]   = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);

  // mobile activities carousel dots
  const [activitySlide, setActivitySlide] = useState(0);

  // mobile blogs/news tab
  const [blogTab, setBlogTab] = useState("blogs");

  const activityChunks = useMemo(() => chunkArray(ACTIVITIES, 6), []);

  useEffect(() => {
    getAllBlogs()
      .then((res) => {
        const arr = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setBlogs(arr.filter((b) => b.status === "approved"));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setNewsLoading(true);
    getBlogs({ category: "All", search: "" })
      .then((res) => setNews(Array.isArray(res.data) ? res.data : res.data?.data || []))
      .catch(() => {})
      .finally(() => setNewsLoading(false));
  }, []);

  useEffect(() => {
    getAllAdImages()
      .then((res) => {
        const data = res.data.data || [];
        const homeSubs = data.filter((i) => i.pageSelected?.includes("Home Page"));
        setBannerSubs(homeSubs.filter((i) => i.plan === "Plan A"));
      })
      .catch(() => {});
  }, []);

  return (
    <>
      <PageMeta
        title="Find Childcare, Classes, Tutors & Activities for Kids"
        description="Kooulu helps parents discover the best childcare centers, camps, activities, tutors, and daycare options for their children."
        keywords="childcare, tutors, activities for kids, kids classes, daycare, camps, Kooulu"
      />

      {/* ── HERO ── */}
      {/* Desktop */}
      <div className="divbg hp-desktop" style={{ backgroundImage: 'url("/assets/images/Header.webp")' }}>
        <div className="hero-container">
          <div className="hero-text">
            <div className="welcometxt">
              <h1 className="heroHeading">
                Find the Best Childcare, Camps, Activities, Tutors, Daycare &amp; More for your Child
              </h1>
              <p className="ptext1">
                One community for all your needs as a parent from infant to toddler to teens and beyond.
              </p>
              <div className="hero-buttons">
                <button className="btn btn-light" onClick={() => navigate("/aboutus")}>
                  <b>Learn More</b>
                </button>
                <a
                  href="/activities"
                  className="explore-activities-btn"
                  onClick={(e) => { e.preventDefault(); navigate("/activities"); }}
                >
                  Explore Activities <span className="arrow">→</span>
                </a>
              </div>
            </div>
          </div>
          <div className="hero-image">
            <picture style={{ display: "contents" }}>
              <source srcSet="/assets/images/MaskGroup77.webp" type="image/webp" />
              <img
                className="kidsimg"
                src="/assets/images/MaskGroup77.png"
                alt="Kids banner"
                width="1920"
                height="1038"
                fetchPriority="high"
              />
            </picture>
          </div>
        </div>
      </div>

      {/* Mobile */}
      <div className="hp-mobile">
        <div className="hero-orange" style={{ backgroundImage: 'url("/assets/images/Header.webp")' }}>
          <h1>Find the Best Camps,<br />Activities, Tutor, Daycare.</h1>
          <p>One community for all your needs as a parent from infant to teens and beyond.</p>
          <div className="hero-buttons">
            <button className="btn btn-light" onClick={() => navigate("/aboutus")}>
              <b>Learn More</b>
            </button>
            <a
              href="/activities"
              className="explore-activities-btn"
              onClick={(e) => { e.preventDefault(); navigate("/activities"); }}
            >
              Explore Activities <span className="arrow">→</span>
            </a>
          </div>
        </div>
        <div className="hero-image-overlap">
          <picture style={{ display: "contents" }}>
            <source srcSet="/assets/images/MaskGroup77.webp" type="image/webp" />
            <img src="/assets/images/MaskGroup77.png" alt="Kids" width="1920" height="1038" />
          </picture>
        </div>
      </div>

      {/* ── ACTIVITIES ── */}
      <div className="activities-section">
        <h2 className="section-title section-title-activities">Activities</h2>

        {/* Desktop grid */}
        <div className="activities-grids">
          {ACTIVITIES.map(({ src, label }) => (
            <div
              key={label}
              className="activity-cards"
              style={{ cursor: "pointer" }}
              onClick={() => navigate(`/activities?category=${encodeURIComponent(label)}`)}
            >
              <img src={src} alt={label} width="35" height="35" />
              <p>{label}</p>
            </div>
          ))}
        </div>

        {/* Mobile carousel */}
        <div className="hp-mobile">
          <div
            className="carousel"
            onScroll={(e) => setActivitySlide(Math.round(e.target.scrollLeft / e.target.offsetWidth))}
          >
            {activityChunks.map((slide, i) => (
              <div className="carousel-slide" key={i}>
                {slide.map((item) => (
                  <div
                    className="activity-cards"
                    key={item.label}
                    onClick={() => navigate(`/activities?category=${encodeURIComponent(item.label)}`)}
                  >
                    <img src={item.img || item.src} alt={item.label} width="28" height="28" />
                    <p>{item.label}</p>
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div className="dots">
            {activityChunks.map((_, i) => (
              <span key={i} className={activitySlide === i ? "dot active" : "dot"} />
            ))}
          </div>
        </div>
      </div>

      <br /><br />

      {/* ── JOIN OUR COMMUNITY ── */}
      <section className="join-community-section">
        <div className="join-row join-row-tint">
          <div className="join-text">
            <h2 className="join-title">School Management</h2>
            <p className="join-desc">
              Manage classes, students, teachers, attendance, and communication—all from one dashboard.
            </p>
            <button className="join-btn" onClick={() => navigate("/partner-registration")}>
              Join as School Admin
            </button>
          </div>
          <div className="join-image">
            <picture style={{ display: "contents" }}>
              <source srcSet="/assets/images/join-community-kids.webp" type="image/webp" />
              <img
                src="/assets/images/join-community-kids.png"
                alt="School management"
                width="657"
                height="397"
                loading="lazy"
              />
            </picture>
          </div>
        </div>

        <div className="join-row join-row-reverse">
          <div className="join-text">
            <h2 className="join-title">Teach as a Freelance Tutor</h2>
            <p className="join-desc">
              Create your profile, set your schedule, and connect with students worldwide.
            </p>
            <button className="join-btn" onClick={() => navigate("/tutor-registration")}>
              Join as Tutor
            </button>
          </div>
          <div className="join-image">
            <picture style={{ display: "contents" }}>
              <source srcSet="/assets/images/join-community-kids.webp" type="image/webp" />
              <img
                src="/assets/images/join-community-kids.png"
                alt="Freelance tutor"
                width="657"
                height="397"
                loading="lazy"
              />
            </picture>
          </div>
        </div>

        <div className="join-row join-row-tint">
          <div className="join-text">
            <h2 className="join-title">Trusted Child Care</h2>
            <p className="join-desc">
              Manage enrollment, caregivers, schedules, and payments effortlessly.
            </p>
            <button className="join-btn" onClick={() => navigate("/childcare/registration")}>
              Join as Child Care
            </button>
          </div>
          <div className="join-image">
            <picture style={{ display: "contents" }}>
              <source srcSet="/assets/images/join-community-kids.webp" type="image/webp" />
              <img
                src="/assets/images/join-community-kids.png"
                alt="Trusted child care"
                width="657"
                height="397"
                loading="lazy"
              />
            </picture>
          </div>
        </div>
      </section>
         <br/><br/>
      <PopularListings />
      <br /><br />
      <Testimonials />
      <br /><br />

      {/* ── ACTIVITY CATEGORIES ── */}
      <ActivityCategories showMobile />

      {/* ── PREFERRED PARTNERS ── */}
      <section className="partners-section">
        <h2 className="section-heading">Preferred Partners</h2>
        <div className="partners-banner">
          <button className="arrow-btnad left" onClick={bannerCarousel.prev} aria-label="Previous partner">
            <FiArrowLeft />
          </button>
          {/*
            Always render the same div>img shape regardless of whether ad data has
            loaded yet or whether any ads exist. Previously this ternary swapped
            between a wrapped <div><img/></div> (ads loaded) and a <picture><source/><img/></picture>
            (no ads), which is a structural mismatch between the react-snap prerendered
            HTML (captured once the ads fetch resolves) and the client's first hydration
            pass (ads always [] before the effect fires) — a likely cause of React error
            #418. A webp <source> can't safely live here either way: <picture> always
            prefers a matching <source> over the <img>, so it would keep showing the
            default banner even once a real ad creative loads. Real ads use the plain
            <img> path; the default banner keeps a fixed width/height for layout
            stability instead.
          */}
          <div className="banner-carousel">
            <img
              src={bannerSubs.length > 0 ? bannerSubs[bannerCarousel.index].image : "/assets/images/bannerad1.webp"}
              alt={bannerSubs.length > 0 ? (bannerSubs[bannerCarousel.index]?.campaignName || "Advertisement") : "Default Banner"}
              className="banner-img"
              width="1094"
              height="429"
            />
          </div>
          <button className="arrow-btnad right" onClick={bannerCarousel.next} aria-label="Next partner">
            <FiArrowRight />
          </button>
        </div>
      </section>

      <br />

      {/* ── BLOGS & NEWS ── */}
      <section className="blogs-news-section">
        {/* Single tabbed layout used at every breakpoint — this used to be
            duplicated with a separate desktop two-column layout above it,
            which rendered both at once on some viewports. */}
        <div className="blogs-news-mobile-tabs" style={{ width: "100%" }}>
          <h2 className="section-title-mobile">Latest Blogs and News</h2>
          <div className="mobile-tabs">
            <button className={`mobiletab ${blogTab === "blogs" ? "active" : ""}`} onClick={() => setBlogTab("blogs")}>Blogs</button>
            <button className={`mobiletab ${blogTab === "news"  ? "active" : ""}`} onClick={() => setBlogTab("news")}>News</button>
          </div>

          {blogTab === "blogs" && (
            <div className="tab-content">
              {/* Fixed-slot rendering (blogs[0], blogs[1], blogs[2]) instead of .slice().map()
                  so the number of rendered cards doesn't vary between the empty initial
                  client state and the populated prerendered snapshot. */}
              <div
                className="main-card"
                onClick={() => blogs[0] && navigate(`/blogdetails/${blogs[0].slug}`)}
                style={{ cursor: blogs[0] ? "pointer" : "default" }}
              >
                <img
                  src={blogs[0] ? getBlogImageUrl(blogs[0].thumbnail_image || blogs[0].banner_image) : "/assets/images/blogimg1.png"}
                  alt={blogs[0]?.blog_title || ""}
                  width="591"
                  height="278"
                />
                <h3 suppressHydrationWarning>{blogs[0]?.blog_title || ""}</h3>
                <p suppressHydrationWarning>{blogs[0]?.blog_content1_text?.slice(0, 120) || ""}...<span className="read-more"> Read more</span></p>
              </div>
              {[blogs[1], blogs[2]].map((blog, i) => (
                <div
                  className="list-card"
                  key={i}
                  onClick={() => blog && navigate(`/blogdetails/${blog.slug}`)}
                  style={{ cursor: blog ? "pointer" : "default" }}
                >
                  <img
                    src={blog ? getBlogImageUrl(blog.thumbnail_image || blog.banner_image) : "/assets/images/blogimg1.png"}
                    alt={blog?.blog_title || ""}
                    width="70"
                    height="70"
                  />
                  <div>
                    <h4 suppressHydrationWarning>{blog?.blog_title || ""}</h4>
                    <p suppressHydrationWarning>{blog?.blog_content1_text?.slice(0, 80) || ""}...</p>
                  </div>
                </div>
              ))}
              <div className="view-all-wrapper">
                <button className="view-all-btn" onClick={() => navigate("/blogs")}>View All</button>
              </div>
            </div>
          )}

          {blogTab === "news" && (
            <div className="tab-content">
              {/* Same fix as the desktop news column: no more newsLoading-gated
                  <p>/fragment swap, and fixed slots (news[0..3]) instead of slice().map(). */}
              <div className="main-card">
                <a href={news[0]?.link || "#"} target="_blank" rel="noreferrer">
                  <img
                    src={news[0]?.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(news[0]?.category || "News")}`}
                    alt={news[0]?.title || ""}
                    width="591"
                    height="278"
                  />
                  <h3 suppressHydrationWarning>{news[0] ? news[0].title : newsLoading ? "Loading news..." : "No news available"}</h3>
                </a>
                <div className="meta-mobile" suppressHydrationWarning><FiCalendar /> {news[0] ? new Date(news[0].pubDate || Date.now()).toLocaleDateString() : ""}</div>
              </div>
              {[news[1], news[2], news[3]].map((item, i) => (
                <div className="list-card" key={i}>
                  <img
                    src={item?.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(item?.category || "News")}`}
                    alt={item?.title || ""}
                    width="70"
                    height="70"
                  />
                  <div>
                    <a href={item?.link || "#"} target="_blank" rel="noreferrer"><h4 suppressHydrationWarning>{item?.title || ""}</h4></a>
                    <div className="meta-mobile" suppressHydrationWarning><FiCalendar /> {item ? new Date(item.pubDate || Date.now()).toLocaleDateString() : ""}</div>
                  </div>
                </div>
              ))}
              <div className="view-all-wrapper">
                <button className="view-all-btn" onClick={() => navigate("/rssfeed")}>View All</button>
              </div>
            </div>
          )}
        </div>
      </section>

      <br />
      <Newsletter />
      <br />
    </>
  );
}

export default Homepage;
